import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Mail, ChevronRight } from 'lucide-react'
import { FormField } from '@components/web/FormField'
import Button from '@components/web/Button'
import OTPInput from '@components/web/OTPInput'
import CarouselSlider, { type CarouselSlide } from '@components/web/CarouselSlider'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { usePageMeta } from '@hooks/usePageMeta'
import { passwordResetService } from '@lib/supabase/passwordReset.service'

const PATIENT_LOGIN_SLIDES: CarouselSlide[] = [
  {
    id: 'doctor-visit',
    headline: 'Book trusted doctors without leaving home',
    subheadline: 'Move from sign-in to care booking in a calmer, simpler flow.',
    backgroundImage: '/Doctor.png',
  },
  {
    id: 'nurse-care',
    headline: 'Get skilled nursing support when it matters most',
    subheadline: 'Stay on top of follow-up care, medication support, and daily health needs.',
    backgroundImage: '/Nurse.png',
  },
  {
    id: 'specialized-care',
    headline: 'Access dependable care built around real life',
    subheadline: 'Keep appointments, chats, and healthcare updates organized in one place.',
    backgroundImage: '/Nurse7.png',
  },
]

const PROVIDER_LOGIN_SLIDES: CarouselSlide[] = [
  {
    id: 'provider-doctor',
    headline: 'Meet patients with confidence and less admin friction',
    subheadline: 'Keep requests, consultations, and follow-ups organized from one dashboard.',
    backgroundImage: '/Doctor.png',
  },
  {
    id: 'provider-nurse',
    headline: 'Show up prepared for every care session',
    subheadline: 'Stay responsive, trustworthy, and in control of your daily workflow.',
    backgroundImage: '/Nurse.png',
  },
  {
    id: 'provider-team',
    headline: 'Build patient trust through a professional workflow',
    subheadline: 'Manage availability, appointments, and communication with less overhead.',
    backgroundImage: '/Nurse7.png',
  },
]

function RoleVisualPanel({ role }: { role: 'patient' | 'provider' }) {
  const isPatient = role === 'patient'
  const slides = isPatient ? PATIENT_LOGIN_SLIDES : PROVIDER_LOGIN_SLIDES

  return (
    <div className="relative hidden lg:flex h-full overflow-hidden border border-white/18 text-white shadow-[0_24px_80px_rgba(0,0,0,0.18)]">
      <div className="absolute left-8 top-8 z-30 inline-flex items-center rounded-full bg-white/12 px-4 py-2 backdrop-blur-md">
        <img src="/logos.png" alt="HomiCareplus" className="h-6 w-auto" />
      </div>
      <CarouselSlider
        slides={slides}
        autoPlayInterval={5200}
        height="h-full"
        className="h-full w-full flex-1"
        indicatorClassName="right-8 top-8 sm:right-8 sm:top-8"
      />
    </div>
  )
}

function AuthCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full rounded bg-[#fcfff8] p-5 shadow-[0_24px_90px_rgba(15,23,42,0.08)] sm:p-6 lg:p-7">
      {children}
    </div>
  )
}

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedRole = searchParams.get('role')
  const role: 'patient' | 'provider' = requestedRole === 'provider' ? 'provider' : 'patient'
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [showForgot, setShowForgot] = useState(false)
  const [forgotStage, setForgotStage] = useState<'request' | 'verify' | 'reset' | 'success'>('request')
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotOtp, setForgotOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotError, setForgotError] = useState('')
  const [forgotNotice, setForgotNotice] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resetSessionExpiresAtMs, setResetSessionExpiresAtMs] = useState<number | null>(null)
  const [resetSessionRemainingMs, setResetSessionRemainingMs] = useState<number | null>(null)

  const patientAuth = usePatientAuth()
  const providerAuth = useProviderAuth()

  usePageMeta({
    title: role === 'provider' ? 'HomiCareplus | Professional Healthcare at Your Doorstep' : 'HomiCareplus | Professional Healthcare at Your Doorstep',
    description:
      role === 'provider'
        ? 'Sign in to manage appointment requests, consultations, earnings, and provider availability on HomiCareplus.'
        : 'Sign in to book appointments, manage your wallet, and stay connected with healthcare providers on HomiCareplus.',
  })

  const normalizeEmail = (value: string) => value.trim().toLowerCase()

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = window.setTimeout(() => setResendCooldown((value) => value - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [resendCooldown])

  useEffect(() => {
    if (forgotStage !== 'reset' || !resetSessionExpiresAtMs) {
      setResetSessionRemainingMs(null)
      return
    }

    const tick = () => {
      const remaining = resetSessionExpiresAtMs - Date.now()
      setResetSessionRemainingMs(Math.max(0, remaining))

      if (remaining <= 0) {
        passwordResetService.clearVerifiedResetToken(role)
        setForgotError('Reset session expired. Please verify a new code.')
        setForgotStage('verify')
      }
    }

    tick()
    const interval = window.setInterval(tick, 1000)
    return () => window.clearInterval(interval)
  }, [forgotStage, resetSessionExpiresAtMs, role])

  const resetForgotFlow = () => {
    passwordResetService.clearVerifiedResetToken(role)
    setShowForgot(false)
    setForgotStage('request')
    setForgotEmail('')
    setForgotOtp('')
    setNewPassword('')
    setConfirmNewPassword('')
    setForgotError('')
    setForgotNotice('')
    setResendCooldown(0)
    setResetSessionExpiresAtMs(null)
    setResetSessionRemainingMs(null)
  }

  const openForgotFlow = () => {
    passwordResetService.clearVerifiedResetToken(role)
    setShowForgot(true)
    setForgotStage('request')
    setForgotOtp('')
    setNewPassword('')
    setConfirmNewPassword('')
    setForgotError('')
    setForgotNotice('')
    setResendCooldown(0)
    setResetSessionExpiresAtMs(null)
    setResetSessionRemainingMs(null)
    setForgotEmail((currentValue) => currentValue || normalizeEmail(email))
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email.trim() || !password) {
      setError('Please enter your email and password')
      return
    }
    setIsLoading(true)

    const result =
      role === 'patient'
        ? await patientAuth.login({ email: normalizeEmail(email), password })
        : await providerAuth.login({ email: normalizeEmail(email), password })

    setIsLoading(false)

    if (!result.success) {
      setError(result.error || 'Sign In failed. Please check your credentials.')
      return
    }

    if (result.redirectTo) {
      navigate(result.redirectTo)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotError('')
    setForgotNotice('')
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address')
      return
    }
    const validateEmail = role === 'patient' ? patientAuth.validateEmail : providerAuth.validateEmail
    if (!validateEmail(normalizeEmail(forgotEmail))) {
      setForgotError('Please enter a valid email address')
      return
    }
    setForgotLoading(true)
    let result: { success: boolean; error?: string }
    try {
      passwordResetService.clearVerifiedResetToken(role)
      result = await passwordResetService.requestOtp(normalizeEmail(forgotEmail), role)
    } finally {
      setForgotLoading(false)
    }
    if (!result.success) {
      setForgotError(result.error || 'Unable to send reset code')
      return
    }
    setForgotNotice('A 6-digit reset code has been sent to your email.')
    setForgotStage('verify')
    setResendCooldown(60)
  }

  const handleVerifyResetOtp = async () => {
    if (forgotOtp.length < 6) {
      setForgotError('Please enter the 6-digit code')
      return
    }
    setForgotError('')
    setForgotNotice('')
    setForgotLoading(true)
    let result: { success: boolean; error?: string }
    try {
      result = await passwordResetService.verifyOtp(normalizeEmail(forgotEmail), forgotOtp, role)
    } finally {
      setForgotLoading(false)
    }
    if (!result.success) {
      setForgotError(result.error || 'Invalid reset code. Please try again.')
      return
    }
    setForgotNotice('Please create a new password.')
    setResetSessionExpiresAtMs(Date.now() + 10 * 60 * 1000)
    setForgotStage('reset')
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotError('')
    setForgotNotice('')
    if (resetSessionRemainingMs === 0) {
      setForgotError('Reset session expired. Please verify a new code.')
      setForgotStage('verify')
      return
    }
    const validatePassword = role === 'patient' ? patientAuth.validatePassword : providerAuth.validatePassword
    const validation = validatePassword(newPassword)
    if (!validation.isValid) {
      setForgotError(validation.errors[0] || 'Please enter a stronger password')
      return
    }
    if (newPassword !== confirmNewPassword) {
      setForgotError('Passwords do not match')
      return
    }

    setForgotLoading(true)
    let result: { success: boolean; error?: string }
    try {
      result = await passwordResetService.resetPassword(normalizeEmail(forgotEmail), newPassword, role)
    } finally {
      setForgotLoading(false)
    }
    if (result.success) {
      setForgotStage('success')
    } else {
      setForgotError(result.error || 'Unable to update password')
    }
  }

  const handleResendResetCode = async () => {
    if (resendCooldown > 0) return
    setForgotError('')
    setForgotNotice('')
    setForgotLoading(true)
    let result: { success: boolean; error?: string }
    try {
      passwordResetService.clearVerifiedResetToken(role)
      setResetSessionExpiresAtMs(null)
      setResetSessionRemainingMs(null)
      result = await passwordResetService.requestOtp(normalizeEmail(forgotEmail), role)
    } finally {
      setForgotLoading(false)
    }
    if (!result.success) {
      setForgotError(result.error || 'Unable to resend code')
      return
    }
    setForgotOtp('')
    setForgotNotice('A new reset code has been sent.')
    setResetSessionExpiresAtMs(null)
    setResetSessionRemainingMs(null)
    setResendCooldown(60)
  }

  if (showForgot) {
    return (
      <div className="grid items-stretch gap-5 lg:h-[calc(100dvh-1.5rem)] lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-5">
        <AuthCard>
          <button onClick={resetForgotFlow} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft size={16} /> Back
          </button>

          <div className="mt-7 mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Reset your password</h1>
            <p className="text-muted-foreground text-sm mt-2">
              {forgotStage === 'request' && 'Enter your email and we’ll send a 6-digit reset code.'}
              {forgotStage === 'verify' && `Enter the 6-digit code sent to ${normalizeEmail(forgotEmail)}.`}
              {forgotStage === 'reset' && 'Create a new password for your account.'}
              {forgotStage === 'success' && 'Your password has been updated successfully.'}
            </p>
          </div>

          {forgotError && (
            <div className="mb-4 rounded border border-destructive/20 bg-destructive/10 px-4 py-3">
              <p className="text-sm text-destructive">{forgotError}</p>
            </div>
          )}

          {!forgotError && forgotNotice && (
            <div className="mb-4 rounded border border-emerald-500/20 bg-emerald-500/10 px-4 py-3">
              <p className="text-sm text-emerald-700">{forgotNotice}</p>
            </div>
          )}

          {forgotStage === 'success' ? (
            <div className="rounded border border-border bg-[#fcfff8] p-6 text-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded bg-primary/10 text-primary">
                <Mail size={28} />
              </div>
              <p className="text-lg font-semibold text-foreground">Password updated</p>
              <p className="text-sm leading-6 text-muted-foreground">You can now sign in with your new password.</p>
              <Button variant="outline" className="w-full" onClick={resetForgotFlow}>Back</Button>
            </div>
          ) : forgotStage === 'request' ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <FormField label="Email Address" type="email" placeholder="your email address" value={forgotEmail} onChange={(e) => { setForgotEmail(e.target.value); setForgotError('') }} required className="h-12 rounded" />
              <Button type="submit" className="w-full h-12 rounded" size="lg" isLoading={forgotLoading}>Send Reset Code</Button>
            </form>
          ) : forgotStage === 'verify' ? (
            <div className="space-y-5">
              <OTPInput value={forgotOtp} onChange={setForgotOtp} error={forgotError || undefined} />
              <Button type="button" onClick={handleVerifyResetOtp} className="w-full h-12 rounded" size="lg" isLoading={forgotLoading}>
                Verify Code
              </Button>
              <div className="text-center text-sm text-muted-foreground">
                {resendCooldown > 0 ? (
                  <p>Resend code in {resendCooldown}s</p>
                ) : (
                  <button type="button" onClick={handleResendResetCode} className="text-primary font-semibold hover:underline" disabled={forgotLoading}>
                    Resend Code
                  </button>
                )}
              </div>
              <button type="button" onClick={() => { setForgotStage('request'); setForgotOtp(''); setForgotError('') }} className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline">
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <FormField label="New Password" type="password" placeholder="Min 12 characters" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setForgotError('') }} required autoComplete="new-password" className="h-12 rounded" />
              <FormField label="Confirm New Password" type="password" placeholder="Re-enter your new password" value={confirmNewPassword} onChange={(e) => { setConfirmNewPassword(e.target.value); setForgotError('') }} required autoComplete="new-password" className="h-12 rounded" />
              {typeof resetSessionRemainingMs === 'number' && (
                <p className="text-xs text-muted-foreground">
                  Reset session expires in {Math.ceil(resetSessionRemainingMs / 1000)}s
                </p>
              )}
              <Button type="submit" className="w-full h-12 rounded" size="lg" isLoading={forgotLoading}>Update Password</Button>
            </form>
          )}
        </AuthCard>

        <RoleVisualPanel role={role} />
      </div>
    )
  }

  return (
    <div className="grid items-stretch gap-5 lg:h-[calc(100dvh-1.5rem)] lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-5">
      <AuthCard>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={16} /> {role === 'patient' ? 'Not a Patient' : 'Not a Provider'}
        </Link>

        <div className="mt-7 mb-6 pb-4 text-center">
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold text-foreground">
            {role === 'patient' ? 'Welcome back' : 'Welcome back'}
          </h1>
          <p className="mx-auto max-w-md text-muted-foreground text-sm mt-2">
            {role === 'patient'
              ? 'Sign in to access your healthcare services.'
              : 'Sign in to manage your appointments, earnings and your provider profile.'}
          </p>
        </div>

        {error && error !== 'Please enter your email and password' && (
          <div className="mb-5 rounded border border-destructive/20 bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4" noValidate>
          <FormField
            label="Email Address"
            type="email"
            placeholder="your email address"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError('') }}
            error={error === 'Please enter your email and password' && !email.trim() ? 'Email is required' : undefined}
            required
            autoComplete="email"
            className="h-12 rounded"
            suffix={<Mail size={18} className="text-muted-foreground" />}
          />
          <FormField
            label="Password"
            type="password"
            placeholder="Your password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError('') }}
            error={error === 'Please enter your email and password' && !password ? 'Password is required' : undefined}
            required
            autoComplete="current-password"
            className="h-12 rounded"
          />

          <div className="flex items-center justify-end">
            <button type="button" onClick={openForgotFlow} className="text-sm text-primary hover:underline">
              Forgot password?
            </button>
          </div>

          <Button type="submit" className="w-full h-12 rounded" size="lg" isLoading={isLoading}>
            {role === 'patient' ? 'Sign In' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 rounded border border-border bg-[#fcfff8] px-4 py-3 text-sm text-muted-foreground">
          <p className="flex items-center justify-between gap-3">
            <span>
              Don&apos;t have an account?
            </span>
            {role === 'patient' ? (
              <Link to="/auth/signup" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                Create account <ChevronRight size={15} />
              </Link>
            ) : (
              <Link to="/auth/provider-signup" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                Create account <ChevronRight size={15} />
              </Link>
            )}
          </p>
        </div>
      </AuthCard>

      <RoleVisualPanel role={role} />
    </div>
  )
}
